/**
 * The Loupe SDK - the half of the tool that lives inside the reviewed app.
 *
 * Q1 (2026-08-20) decided the injected SDK over a screenshot overlay and over
 * a same-origin proxy: real DOM anchors, and nothing standing in front of the
 * app under review. Q10 (2026-08-21) gave it a second way to reach Loupe:
 *
 * - **Framed.** Loupe's shell contains the app; messages go by postMessage and
 *   the shell draws the chrome. Right when Loupe is hosted beside the app.
 * - **Top-level.** The dashboard *opened* this window, so the browser keeps
 *   the application's own session cookie - which a cross-site frame drops.
 *   Messages go through the server over a WebSocket, and the chrome is drawn
 *   here, because the reviewer is looking at this window and not at another
 *   one.
 *
 * Include it in a NON-PRODUCTION build only:
 *
 *   <script src="http://localhost:4173/loupe-sdk.js"
 *           data-loupe-build="rev-a1b2c3"></script>
 *
 * It is inert until a Loupe shell says hello or a dashboard hands it a
 * connection, so a developer who forgets to remove it from a dev build gets an
 * idle script rather than a recorder.
 */

import { record } from "rrweb";
import { snapshot } from "rrweb-snapshot";

import { anchorFor, documentRect, IGNORE_CLASS, isOurs, isRendered, isStaleAnchor, resolveAnchor, type Resolution } from "./anchor";
import { createChrome } from "./chrome";
import {
  entryTitle,
  LOUPE_PROTOCOL,
  pinCentre,
  type Anchor,
  type Mode,
  type NoteKind,
  type PinInfo,
  type SdkMessage,
  type ShellMessage,
  type ShowViewResult,
  type ViewState,
} from "./protocol";
import { frameTransport, socketTransport, type SocketState, type Transport } from "./transport";
import { DEFAULT_PREFS, matchesCombo, switchClick, switchHeld, type ReviewPrefs } from "../shared/review-prefs";
import { describeClient, readAppContext } from "../shared/app-context";
import { freezeResources, resourceUrls, type FrozenResource } from "../shared/still-resources";

type Pin = PinInfo;

interface Credentials {
  connection: string;
  token: string;
  origin: string;
}

const script = document.currentScript as HTMLScriptElement | null;
const BUILD = script?.dataset.loupeBuild ?? null;
/**
 * Capture policy. Q5 (2026-08-21) chose full-fidelity capture on concept
 * sec 9's stated assumption - lead developer and PO see everything, and the
 * assumption expires before Loupe is pointed anywhere else. The switch stays
 * here because that is the day it has to be flipped, not rebuilt:
 *
 *   data-loupe-mask="all"  ->  input values are recorded as asterisks
 */
const MASK_INPUTS = script?.dataset.loupeMask === "all";
/** Renamed with protocol 6: a window still holding a pass from before 0009 starts inert rather than failing. */
const STORAGE_KEY = "loupe.connection";

let transport: Transport | null = null;
let chrome: ReturnType<typeof createChrome> | null = null;
let credentials: Credentials | null = null;
let mode: Mode = "browse";
/** Protocol 7: the reviewer's keys and marks; the defaults until the shell says otherwise. */
let prefs: ReviewPrefs = DEFAULT_PREFS;
/** Whether the temporary switch's modifiers are down right now - the surface shows the OTHER mode while they are. */
let holding = false;
let pins: Pin[] = [];
let pending: { anchor: Anchor; view: ViewState; recordingId: string | null; snapshot: unknown; resources?: FrozenResource[] } | null = null;

let stopRecording: (() => void) | null = null;
let events: unknown[] = [];
let recordStartedAt = "";

// ---------------------------------------------------------------- chrome

const layer = document.createElement("div");
layer.className = IGNORE_CLASS;
layer.setAttribute("data-loupe", "layer");

const highlight = document.createElement("div");
highlight.className = IGNORE_CLASS;

/**
 * How Kommentieren announces itself (the notes of 2026-09-25): nothing, a bar
 * along the bottom of the viewport, or a frame around it - in the reviewer's
 * colour and width. Drawn by the SDK and not by the shell, because on the
 * window surface the shell is in another window and cannot draw here.
 */
const indicator = document.createElement("div");
indicator.className = IGNORE_CLASS;
indicator.setAttribute("data-loupe", "indicator");

/**
 * The frame around an entry's element after the shell asked for its view
 * (D-049, [F-008]) - so the reviewer sees which element the entry is about,
 * not only that the page is the right one. In the pin's colour, because it
 * marks what a pin would mark.
 */
const focusBox = document.createElement("div");
focusBox.className = IGNORE_CLASS;
focusBox.setAttribute("data-loupe", "focus");

const style = document.createElement("style");
style.className = IGNORE_CLASS;
style.textContent = `
[data-loupe="layer"] {
  position: absolute; top: 0; left: 0; width: 0; height: 0;
  z-index: 2147483000; pointer-events: none;
  font: 12px/1.2 system-ui, sans-serif;
}
[data-loupe="layer"] * { box-sizing: border-box; }
[data-loupe="indicator"] { position: fixed; pointer-events: none; display: none; z-index: 2147483001; }
[data-loupe="layer"][data-pins="false"] [data-loupe="pin"] { display: none; }
/*
 * D-066: a recording takes no pin click (setPeek), so it shows no pin either. A
 * pin left standing looked clickable and handed the click to the element under
 * it - on the Incident Tool's *Vorfall erfassen* that opened a new incident.
 */
[data-loupe="layer"][data-recording="true"] [data-loupe="pin"] { display: none; }
[data-loupe="highlight"] {
  position: absolute; pointer-events: none;
  border: 2px solid #d43f3a; background: rgba(212, 63, 58, 0.10);
  border-radius: 2px; transition: all 60ms linear;
}
/*
 * A pin sits ON what it points at since V0.4 ([F-001], AC-091), which changes
 * what it may do to a click. At the element's corner it was mostly out of the
 * way; over the spot it covers exactly the place the reviewer aims at.
 *
 * So the pin is passive by default and becomes a click target only in
 * annotate mode - where the SDK swallows application clicks anyway ([F-003]),
 * so nothing is taken from the application that it would otherwise have had.
 * In browse mode the reviewer is operating somebody else's software and the
 * review tool is a guest: it does not stand in front of a button.
 */
[data-loupe="pin"] {
  position: absolute; pointer-events: none; cursor: pointer;
  min-width: 22px; height: 22px; padding: 0 6px;
  display: flex; align-items: center; justify-content: center;
  background: var(--loupe-pin, #d43f3a); color: #fff; border-radius: 11px;
  border: 2px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,.4);
  font-weight: 600; transform: translate(-50%, -50%);
}
[data-loupe="layer"][data-mode="annotate"] [data-loupe="pin"] { pointer-events: auto; }
/*
 * In browse mode a pin takes the click when data-peek says so: always since
 * 2026-09-25 (the pins can be hidden now, so one in the way is one switch
 * away), or only while Alt is held for a reviewer who chose that - the
 * modifier saying "I mean the review, not the application". Shift+Ctrl was
 * that modifier from the trial of 2026-09-10 until then.
 */
[data-loupe="layer"][data-peek="true"] [data-loupe="pin"] { pointer-events: auto; }
[data-loupe="focus"] {
  position: absolute; pointer-events: none; display: none;
  border: 3px solid var(--loupe-pin, #d43f3a); border-radius: 3px;
  box-shadow: 0 0 0 3px rgba(255,255,255,.75), 0 2px 14px rgba(0,0,0,.35);
}
[data-loupe="pin"][data-stale="true"] { background: #8a8a8a; }
[data-loupe="pin"][data-pending="true"] { background: #1f7a4d; }
/*
 * The crosshair is the APPLICATION's cursor while picking, and never ours.
 * It used to be \`[data-loupe="annotating"] *\` - every element under the
 * body, which includes the editor, its buttons and the review bar - so the
 * crosshair stood over the editor right after the click it opened (root
 * operator, 2026-09-15). Loupe's own nodes keep their own cursors; the pin's
 * pointer needed an override of its own before, and now needs none.
 */
[data-loupe="annotating"] *:not(.${IGNORE_CLASS}, .${IGNORE_CLASS} *) { cursor: crosshair !important; }
/*
 * The entry's title beside its pin, on hover. Hidden by default rather than
 * drawn and faded: a page with twenty notes would otherwise be twenty labels.
 */
[data-loupe="pin-title"] {
  display: none; position: absolute; left: calc(100% + 6px); top: 50%;
  transform: translateY(-50%); white-space: nowrap; max-width: 320px;
  overflow: hidden; text-overflow: ellipsis;
  background: #1c2229; color: #e8ecf1; border: 1px solid #2c343d;
  border-radius: 4px; padding: 4px 8px; font-weight: 500;
  box-shadow: 0 2px 8px rgba(0,0,0,.35);
}
/* Near the right edge the title opens to the left - on the right it would
   widen the page for as long as the pointer rests on the pin. */
[data-loupe="pin"][data-side="left"] [data-loupe="pin-title"] { left: auto; right: calc(100% + 6px); }
[data-loupe="pin"]:hover [data-loupe="pin-title"] { display: block; }
`;

/** How far a pin's title reaches past its pin - the gap, `max-width` above, the border. */
const TITLE_REACH = 6 + 320 + 2;

highlight.setAttribute("data-loupe", "highlight");
highlight.style.display = "none";

// ---------------------------------------------------------------- state

function viewState(): ViewState {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return {
    href: location.href,
    route: location.pathname + location.search + location.hash,
    title: document.title,
    viewport: {
      w,
      h,
      dpr: window.devicePixelRatio || 1,
      orientation: w >= h ? "landscape" : "portrait",
    },
    // Protocol 3: the viewer reopens the still where the reviewer was looking.
    scroll: { x: Math.round(window.scrollX), y: Math.round(window.scrollY) },
    build: BUILD,
    // F-013 (D-054): read now, not at load - a person logs in, out and between
    // accounts while the page stays, and the entry must say who it was then.
    ...appAndClient(),
    capturedAt: new Date().toISOString(),
  };
}

/** What the application says about its account, and what the browser is. */
function appAndClient(): Pick<ViewState, "app" | "client"> {
  const app = script ? readAppContext(script.dataset) : undefined;
  const client = describeClient(navigator.userAgent, navigator.language);
  return { ...(app ? { app } : {}), ...(Object.keys(client).length > 0 ? { client } : {}) };
}

/**
 * The application rewrites its attributes on login, logout and every change of
 * role (F-013: *"Änderungen dieser Rollen während einer laufenden Prüfung"*).
 * The shell learns of each through the same message as a navigation, so the
 * view it holds is never older than the account in front of it.
 */
function watchAppStatement(): void {
  if (!script) return;
  new MutationObserver(() => post({ type: "loupe:navigated", view: viewState() })).observe(script, {
    attributes: true,
    attributeFilter: ["data-loupe-role", "data-loupe-user", "data-loupe-context"],
  });
}

function post(message: SdkMessage): void {
  transport?.send(message);
}

// ---------------------------------------------------------------- connection

function asOrigin(value: string): string {
  try {
    return new URL(value).origin;
  } catch {
    return value;
  }
}

function loupeOrigin(fromUrl: string | null): string {
  if (script?.dataset.loupeOrigin) return script.dataset.loupeOrigin;
  if (fromUrl) return fromUrl;
  // Last resort: wherever this script came from. Wrong when the SDK is proxied
  // through the app's own dev server, which is exactly why the dashboard sends
  // its origin along when it opens the window.
  try {
    return new URL(script?.src ?? location.href).origin;
  } catch {
    return location.origin;
  }
}

/**
 * The dashboard hands the connection over in the URL, because that is the only
 * channel into a page on somebody else's origin. It is read once and moved
 * into sessionStorage, so it survives the app's own navigation - and the URL
 * is cleaned immediately, so our parameters never end up in a screenshot, a
 * bookmark, or the application's router.
 */
function readCredentials(): Credentials | null {
  const url = new URL(location.href);
  const connection = url.searchParams.get("loupe_connection");
  const token = url.searchParams.get("loupe_token");

  if (connection && token) {
    const found: Credentials = {
      connection,
      token,
      origin: loupeOrigin(url.searchParams.get("loupe_origin")),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(found));
    for (const key of ["loupe_connection", "loupe_token", "loupe_origin"]) {
      url.searchParams.delete(key);
    }
    history.replaceState(history.state, "", url.toString());
    return found;
  }

  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as Credentials;
  } catch {
    return null;
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${credentials!.origin}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      // A hosted Loupe is behind a shared-secret gate that this page cannot
      // satisfy - it is somebody else's origin and holds no cookie of ours.
      // The connection's token is what lets the agent in, and only to its own
      // connection.
      "x-loupe-token": credentials!.token,
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) throw new Error(`${response.status}`);
  return (await response.json()) as T;
}

// ---------------------------------------------------------------- pins

/**
 * The pins on the page and what each sits on, kept so a scroll can move them
 * without drawing them again (D-059). `el` is null for the pending pin, which
 * stays where the click was.
 */
let placedPins: Array<{ marker: HTMLElement; el: Element | null; rect: Anchor["rect"]; spot: Anchor["spot"] }> = [];
/** The page's extent as `drawPins` last measured it - see there for why not on every scroll. */
let pageExtent = { w: 0, h: 0 };

function drawPins(): void {
  layer.querySelectorAll('[data-loupe="pin"]').forEach((n) => n.remove());
  const stale: string[] = [];

  /*
   * The page's own extent, read with our pins gone - a pin that already
   * widened the page must not be the measure it is kept inside (pinCentre).
   */
  pageExtent = { w: document.documentElement.scrollWidth, h: document.documentElement.scrollHeight };
  const placed: typeof placedPins = [];

  let shown = 0;

  const here = viewState().route;

  for (const pin of pins) {
    const found = resolveAnchor(pin.anchor);

    // D-026: the rule is in anchor.ts, where it can be asserted without a
    // browser. The two cases the comment below separates for DRAWING were
    // counted as one here, which put the badge on warn permanently.
    if (isStaleAnchor(found, { capturedRoute: capturedRoute.get(pin.id), here })) stale.push(pin.id);

    /*
     * D-003: a pin is drawn only where its own element is on the page right
     * now. Two ways it can fail, and they used to be treated differently for
     * no reason:
     *
     *   - the element GENERATES NO BOX - a closed tab, a folded section
     *     ([F-001] V0.4 / AC-092). Its rect is all zeros, so drawing it would
     *     park the marker in the viewport's corner.
     *   - the element IS NOT THERE - another page entirely. This one fell
     *     through the old guard and was drawn at the coordinates it was
     *     CAPTURED at, which is how every note of a session ended up painted
     *     over a page it was never written on.
     *
     * The second is worse than a stray marker: the position is meaningless and
     * the marker looks like an answer. Neither is drawn now.
     *
     * Nothing is lost by not drawing. `loupe:anchor-status` still reports the
     * stale set below, and the shell badges those notes in the item list - the
     * note is findable, it just no longer claims a place on a page it does not
     * belong to. Scrolling needs no rule of its own: the layer is positioned in
     * document space, so a pin leaves the viewport with its element.
     */
    if (!found.el || !isRendered(found.el)) continue;

    const el = found.el;
    const rect = documentRect(el);
    shown += 1;

    const marker = document.createElement("div");
    marker.className = IGNORE_CLASS;
    marker.setAttribute("data-loupe", "pin");
    // Grey rather than red: the element was found by a WEAKER strategy than
    // the one that captured it, so it may not be the same element.
    marker.dataset.stale = String(found.stale);
    marker.dataset.entry = pin.id;
    marker.textContent = pin.label;
    // The title beside the pin on hover (trial 2026-09-10). A child, not the
    // native `title` tooltip: that one appears late, cannot be styled, and
    // would show a second label on top of this one.
    const title = document.createElement("span");
    title.setAttribute("data-loupe", "pin-title");
    title.textContent = `${pin.id} · ${pin.title || pin.anchor.text || pin.anchor.selector}`;
    marker.appendChild(title);
    marker.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      openEntry(pin);
    });
    // [F-001] V0.4 / AC-091: the pin sits where the reviewer clicked, held as
    // a fraction of the element's box so it survives a reflow. An anchor from
    // before V0.4 carries no spot and keeps the old top-right corner.
    placed.push({ marker, el, rect, spot: pin.anchor.spot });
    layer.appendChild(marker);
  }

  if (pending) {
    const marker = document.createElement("div");
    marker.className = IGNORE_CLASS;
    marker.setAttribute("data-loupe", "pin");
    marker.dataset.pending = "true";
    marker.textContent = "*";
    placed.push({ marker, el: null, rect: pending.anchor.rect, spot: null });
    layer.appendChild(marker);
  }

  placedPins = placed;
  placePins();

  post({ type: "loupe:anchor-status", stale });
  // What is on THIS page, out of the connection's notes. One number would have to
  // lie in one direction or the other once pins are filtered.
  chrome?.setCount(shown, pins.length);
}

/**
 * Put every drawn pin where its element is NOW (D-059).
 *
 * The layer is in document space, which is right for an element that scrolls
 * with the page and wrong for one inside a `position: fixed` or `sticky` box or
 * a scrolling panel: those move against the document whenever something
 * scrolls, and a pin placed once stayed behind. So a scroll anywhere - heard in
 * the capture phase, which an inner scroller's scroll reaches too - places the
 * pins again, at most once per frame, without drawing them again: `drawPins`
 * resolves anchors and reports, and a scroll changes neither.
 *
 * The extent is the one `drawPins` measured with the pins gone; a scroll does
 * not change it, and measuring with the pins present is what pinCentre's rule
 * exists to avoid.
 */
function placePins(): void {
  // Every rect and size read before any position is written: one layout, not one per pin.
  const rects = placedPins.map(({ el, rect }) => (el && el.isConnected ? documentRect(el) : rect));
  const sizes = placedPins.map(({ marker }) => ({ w: marker.offsetWidth, h: marker.offsetHeight }));
  placedPins.forEach(({ marker, spot }, index) => {
    const at = pinCentre(rects[index], spot, sizes[index], pageExtent);
    marker.style.left = `${at.x}px`;
    marker.style.top = `${at.y}px`;
    if (at.x + sizes[index].w / 2 + TITLE_REACH > pageExtent.w) marker.dataset.side = "left";
    else delete marker.dataset.side;
  });
  placeFocus();
}

let placeQueued = false;
function onScroll(): void {
  if (placeQueued) return;
  placeQueued = true;
  requestAnimationFrame(() => {
    placeQueued = false;
    placePins();
  });
}

interface LoadedNote {
  id: string;
  anchor: Anchor;
  body: string;
  kind: NoteKind;
  author_id: string | null;
  /** Where it was written. Absent on a pin the shell hands us (`loupe:pins`). */
  view?: { route?: string };
}

/**
 * The route each entry was captured on, by entry id - what lets `drawPins`
 * tell "this anchor broke" from "this entry belongs to another page" (D-026).
 *
 * Kept beside the pins rather than on them: a `PinInfo` crosses the protocol,
 * and this is a fact the SDK reads once from its own load. An entry whose
 * route we do not know is simply never counted, which is the honest answer.
 */
const capturedRoute = new Map<string, string>();

/** An entry as a pin. `own` is what decides whether a click opens the editor. */
const pinFor = (note: LoadedNote, account: string | null): Pin => ({
  id: note.id,
  label: note.id.replace("LP-", ""),
  anchor: note.anchor,
  title: entryTitle(note.body),
  body: note.body,
  kind: note.kind,
  own: account !== null && note.author_id === account,
});

/** The connection's account - the person this token stands in for. */
let accountId: string | null = null;
/** The connection's project - where the bar's mark opens Loupe. */
let projectId: string | null = null;

async function loadPins(): Promise<void> {
  if (!credentials) return;
  const loaded = await api<{ connection: { account_id: string; project_id: string }; notes: LoadedNote[] }>(
    `/api/connections/${credentials.connection}`,
  );
  accountId = loaded.connection.account_id;
  projectId = loaded.connection.project_id;
  pins = loaded.notes.map((note) => pinFor(note, accountId));
  for (const note of loaded.notes) {
    if (note.view?.route) capturedRoute.set(note.id, note.view.route);
  }
  drawPins();
}

/**
 * A pin was clicked - in annotate mode, or with Shift+Ctrl in browse mode
 * (trial 2026-09-10: a left click opens the entry).
 *
 * Who opens it depends on who owns the screen. Inside a frame the shell draws
 * every dialog, so it is told. In the application's own window the reviewer
 * is looking HERE, and the SDK's chrome opens it: the editor for their own
 * entry, a reading card for anybody else's.
 */
function openEntry(pin: Pin): void {
  post({ type: "loupe:pin-clicked", id: pin.id });
  if (transport?.kind !== "socket" || !chrome) return;
  if (isWritingNote()) return; // a note is being written; do not trade it for another
  highlight.style.display = "none";
  // Read first, always (2026-09-25); one's own entry is edited from the card, not by the click.
  const edit = pin.own
    ? () =>
        chrome?.openEditor({
          anchor: pin.anchor,
          view: viewState(),
          withRecording: false,
          existing: { id: pin.id, body: pin.body ?? "", kind: pin.kind ?? "defect" },
        })
    : undefined;
  chrome.openReader({ id: pin.id, body: pin.body ?? pin.title ?? "", kind: pin.kind ?? null }, edit);
}

// ---------------------------------------------------------------- modes

/**
 * Is a recording running? While it is, the tool is Browsen and stays Browsen
 * (root operator, 2026-09-15): stopping a recording opens an editor to ask
 * what was reproduced, and a note already being written in Kommentieren would
 * be pushed out of the one editor there is. Until that has a concept of its
 * own, the two simply do not overlap - a recording starts from Browsen, and
 * nothing leaves Browsen until it stops.
 *
 * Held here and not only in the two bars: both send commands, and the rule
 * has to hold whichever of them does.
 */
const isRecording = (): boolean => stopRecording !== null;

function setMode(next: Mode): void {
  if (isRecording() && next !== "browse") {
    chrome?.setMode(mode); // whoever asked is shown the tool that still holds
    return;
  }
  mode = next;
  setPeek(pinsTakeClicks());
  showEffective();
  chrome?.setMode(mode);
}

/**
 * The mode the surface shows: the chosen one - or, while the temporary
 * switch's modifiers are down, the other, so the reviewer sees what the click
 * will do before making it. Never while recording - a recording keeps Browsen,
 * which is what `isRecording` keeps apart from an editor.
 */
function effective(): Mode {
  if (!holding || isRecording()) return mode;
  return mode === "browse" ? "annotate" : "browse";
}

/**
 * The mode a pointer event gets: the other one when it IS the temporary switch
 * - its modifiers and its button (the notes of 2026-09-28, *Temporärer Modus
 * Wechsel*: in Browsen Strg+Linksklick writes an entry at once, in
 * Kommentieren it is the click Browsen would have made). Decided by the event
 * and not by `holding`, because a side button has no modifier to hold. Any
 * other event - a submit - gets what the surface shows.
 */
function modeFor(e: Event): Mode {
  if (!(e instanceof MouseEvent)) return effective();
  if (isRecording() || !switchClick(e, prefs.hold)) return mode;
  return mode === "browse" ? "annotate" : "browse";
}

/** Everything that shows the mode, drawn from the effective one. */
function showEffective(): void {
  const now = effective();
  // Decides whether a pin may take a click; see the pin's own CSS above.
  layer.setAttribute("data-mode", now);
  if (now === "annotate") {
    document.body.setAttribute("data-loupe", "annotating");
  } else {
    document.body.removeAttribute("data-loupe");
    highlight.style.display = "none";
  }
  drawIndicator(now);
}

function drawIndicator(now: Mode): void {
  const s = indicator.style;
  if (now !== "annotate" || prefs.indicator === "none") {
    s.display = "none";
    return;
  }
  s.display = "block";
  if (prefs.indicator === "bottom") {
    Object.assign(s, { left: "0", right: "0", bottom: "0", top: "auto", height: `${prefs.width}px`, border: "0", background: prefs.color });
  } else {
    Object.assign(s, { left: "0", right: "0", top: "0", bottom: "0", height: "auto", background: "transparent", border: `${prefs.width}px solid ${prefs.color}` });
  }
}

/** The switch's modifiers, read from every key and pointer event - the pointer carries them wherever the focus is. */
function holdFrom(e: MouseEvent | KeyboardEvent): void {
  const next = switchHeld(e, prefs.hold);
  if (next === holding) return;
  holding = next;
  showEffective();
}

/**
 * The toggle key pressed in the application. On the window surface the SDK
 * owns the mode and switches it; inside a frame the shell does, so it is told.
 */
function onKey(e: KeyboardEvent): void {
  if (e.type !== "keydown" || !matchesCombo(e, prefs.toggle)) return;
  e.preventDefault();
  e.stopPropagation();
  if (transport?.kind === "socket") setMode(mode === "browse" ? "annotate" : "browse");
  else post({ type: "loupe:toggle-mode" });
}

/**
 * Is a note being written right now? [F-006]: while the editor is open the
 * selection is PAUSED.
 *
 * It has to be read from BOTH places, because the two surfaces put the editor
 * in different hands: over a socket the SDK's own chrome owns it
 * (`isEditing`), inside a frame the dashboard owns it and the SDK only knows
 * it holds a `pending` anchor.
 *
 * A predicate rather than the condition written out twice - which is how this
 * drifted in the first place. D-002 fixed the CLICK half in September and left
 * the VISIBLE half behind: the highlight went on following the cursor while
 * the editor was open, so the surface said "pick something" while the click
 * that would pick it was being swallowed. Answering one of the two questions
 * differently from the other is the bug, so there is now only one place to
 * answer it.
 */
function isWritingNote(): boolean {
  return Boolean(chrome?.isEditing()) || pending !== null;
}

/**
 * Whether the pins take a click in browse mode - always since 2026-09-25, or
 * only while Alt is held (`prefs.pinClick`); Shift+Ctrl until then (trial
 * 2026-09-10). Read from every pointer event rather than only from key events: inside a
 * frame the keyboard focus is usually in the shell, and a keydown never
 * reaches this document - the pointer event carries the modifiers regardless.
 */
function setPeek(on: boolean): void {
  // Not while recording: a pin opens an editor, which is what isRecording keeps apart.
  const next = on && mode === "browse" && !isRecording();
  if ((layer.getAttribute("data-peek") === "true") !== next) layer.setAttribute("data-peek", String(next));
}

/*
 * Since 2026-09-25 a shown pin takes a plain click in Browsen too - *"this is
 * the most natural thing that a user want's to do if they see it"*, and a pin
 * that is in the way can be hidden now. A reviewer who wants the application's
 * click under a pin chooses "only with Alt" in the Einstellungen; Umschalt+Strg
 * was that modifier before, and went with it.
 */
const pinsTakeClicks = (e?: MouseEvent | KeyboardEvent): boolean => prefs.pinClick === "always" || Boolean(e?.altKey);
const peekFrom = (e: MouseEvent | KeyboardEvent): void => setPeek(pinsTakeClicks(e));

function onMove(e: MouseEvent): void {
  peekFrom(e);
  holdFrom(e);
  if (effective() !== "annotate") return;
  // Paused: stop pointing at what a click would no longer take.
  if (isWritingNote()) {
    highlight.style.display = "none";
    return;
  }
  const el = e.target as Element | null;
  if (!el) return;
  /*
   * Over a pin - or anything else of ours - the red frame goes (trial
   * 2026-09-10). It used to stay on the last application element the cursor
   * crossed, so hovering a pin showed a selection the click would not make:
   * the click opens the entry, it does not anchor a new one.
   */
  if (isOurs(el)) {
    highlight.style.display = "none";
    return;
  }
  const rect = documentRect(el);
  highlight.style.display = "block";
  highlight.style.left = `${rect.x}px`;
  highlight.style.top = `${rect.y}px`;
  highlight.style.width = `${rect.w}px`;
  highlight.style.height = `${rect.h}px`;
}

/**
 * In annotate mode the app must not act on the click. Swallowing it in the
 * capture phase is what makes a review pass over a working app safe: the
 * reviewer can point at a delete button without deleting anything.
 */
function swallow(e: Event): boolean {
  if (e instanceof MouseEvent) holdFrom(e);
  if (modeFor(e) !== "annotate") return false;
  if (isOurs(e.target as Node)) return false;
  e.preventDefault();
  e.stopPropagation();
  return true;
}

/**
 * A button other than the left one writes an entry only when it is the
 * temporary switch - a right click in Kommentieren is not an entry. The
 * `contextmenu` and `mouseup` of that click are swallowed with it: the first
 * would open the application's menu over the editor, the second is where a
 * browser goes back or forward for the side buttons.
 */
function onAuxClick(e: MouseEvent): void {
  if (switchClick(e, prefs.hold)) onClick(e);
}
function swallowSwitch(e: MouseEvent): void {
  if (switchClick(e, prefs.hold)) swallow(e);
}

function onClick(e: MouseEvent): void {
  if (!swallow(e)) return;

  /*
   * [F-006] V0.4 / AC-093: while the editor is open the selection is PAUSED.
   * The click is still swallowed above - the review never operates the
   * application it is reviewing ([F-003]) - it simply does not re-point the
   * note being written, and the text in the box survives.
   *
   * The state is read rather than kept beside the editor, so "paused" and
   * "a note is being written" cannot drift apart - and it has to be read from
   * BOTH places, because the two surfaces put the editor in different hands:
   * over a socket the SDK's own chrome owns it (`isEditing`), inside a frame
   * the dashboard owns it and the SDK only knows it has a `pending` anchor.
   * Reading one of the two would have left [F-006] holding on one surface
   * and not the other, which is how D-002 survived being specified in V0.3.
   *
   * Every way out - ESC, Verwerfen, Speichern, `loupe:cancel-pending` - clears
   * one of the two and re-arms the selection.
   */
  if (isWritingNote()) return;

  const anchor = anchorFor(e.target as Element, { clientX: e.clientX, clientY: e.clientY });
  const view = viewState();
  /*
   * F-009, and the moment matters: the still is taken HERE, when the reviewer
   * picks the element - not when they press Speichern. "Der Zustand der
   * Ansicht" is the state they were looking at and reacting to; between the
   * click and the save they may type for a minute while the page moves on
   * underneath. Capturing late would store a view nobody ever commented on.
   *
   * It also unifies the two surfaces. Over a socket the SDK saves the note
   * itself; inside a frame the dashboard does, and the dashboard cannot reach
   * the application's DOM at all. One capture on `pending` serves both.
   */
  const still = takeStill();
  pending = { anchor, view, recordingId: null, snapshot: still };
  // Not on the next mousemove - now. The editor is opening under a cursor that
  // may never move again, and a highlight left standing says the selection is
  // live while the click that would use it is being swallowed.
  highlight.style.display = "none";
  drawPins();

  if (transport?.kind === "socket") {
    chrome?.openEditor({ anchor, view, withRecording: false });
  } else {
    post({ type: "loupe:anchor", anchor, view, snapshot: still });
  }
  void freezeStill(still, view);
}

/**
 * D-033: the still's resources, frozen - fetched here, in the application's
 * own page, where the browser has most of them cached and may read them; the
 * shell on Loupe's origin could not. Sent after the anchor, never before it:
 * the editor opens at once, and an entry saved before they arrive keeps its
 * URLs (the store is `server/resources.ts`).
 */
async function freezeStill(still: unknown, view: ViewState): Promise<void> {
  if (!still) return;
  const resources = await freezeResources(resourceUrls(still));
  if (resources.length === 0) return;
  if (pending && pending.view.capturedAt === view.capturedAt) pending.resources = resources;
  if (transport?.kind !== "socket") post({ type: "loupe:still-resources", capturedAt: view.capturedAt, resources });
}

/**
 * Sends what the store lacks and returns the still with its map - or the
 * still as it was, if anything fails: a reviewer's entry is never held back
 * for its pictures.
 */
async function withFrozen(still: unknown, resources: FrozenResource[] | undefined): Promise<unknown> {
  if (!still || !resources?.length || !credentials) return still;
  try {
    const { missing } = await api<{ missing: string[] }>(`/api/connections/${credentials.connection}/resources/missing`, {
      method: "POST",
      body: JSON.stringify({ hashes: resources.map((r) => r.hash) }),
    });
    const wanted = new Set(missing);
    for (const r of resources) {
      if (!wanted.has(r.hash)) continue;
      await api(`/api/connections/${credentials.connection}/resources/${r.hash}`, { method: "PUT", body: JSON.stringify({ type: r.type, data: r.data }) });
      wanted.delete(r.hash);
    }
    return { ...(still as object), loupe_resources: Object.fromEntries(resources.map((r) => [r.url, r.hash])) };
  } catch {
    return still;
  }
}

// ---------------------------------------------------------------- notes

/**
 * The still (F-009): the document tree as it stood, serialized.
 *
 * A tree and not a raster image, for two reasons the SRS is explicit about:
 * the rebuilt tree carries selectable text, so the development sees WHAT stood
 * there rather than only how it looked; and it carries the masking through,
 * because masking is a property of the tree and does not survive rasterisation
 * (NF-008).
 *
 * The options mirror `record()` deliberately. If the recorder blocks Loupe's
 * own chrome and masks inputs, a still that did neither would be a second,
 * laxer capture path out of the same page - and the laxer one is the one that
 * leaks.
 *
 * `inlineStylesheet` is what makes the rebuild independent: a cross-origin
 * stylesheet cannot be fetched later from the shell, so a still that only
 * referenced it would rebuild unstyled and look like a defect that is not one.
 *
 * Never throws. A note the reviewer has typed must not be lost because a
 * document resisted serialization - it saves without a still, and the shell
 * says so.
 */
function takeStill(): unknown | null {
  try {
    return (
      snapshot(document, {
        blockClass: IGNORE_CLASS,
        maskAllInputs: MASK_INPUTS,
        inlineStylesheet: true,
        recordCanvas: false,
      }) ?? null
    );
  } catch {
    return null;
  }
}

async function saveNote(note: { body: string; kind: NoteKind }): Promise<void> {
  if (!credentials || !pending) return;
  /*
   * D-021: the selection is live again the moment the editor closes ([F-006]
   * V0.4: "auf welchem Weg auch immer") - and the chrome closes it on
   * Speichern, before this request is answered. `pending` used to be cleared
   * only after the answer, so for one round trip the editor was gone and a
   * click was still swallowed as if it were open.
   */
  const taken = pending;
  pending = null;
  drawPins();
  let created: { id: string; anchor: Anchor };
  try {
    created = await api<{ id: string; anchor: Anchor }>(
      `/api/connections/${credentials.connection}/notes`,
      {
        method: "POST",
        body: JSON.stringify({
          body: note.body,
          kind: note.kind,
          anchor: taken.anchor,
          view: taken.view,
          recording_id: taken.recordingId,
          snapshot: await withFrozen(taken.snapshot, taken.resources),
        }),
      },
    );
  } catch {
    /*
     * [NF-012]: what was typed survives. The editor closed on Speichern before
     * the answer, so a save that fails - Loupe restarting under it (D-024), a
     * network gone - used to take the text with it. It comes back, with the
     * same anchor, and says why.
     */
    pending = taken;
    drawPins();
    chrome?.openEditor({ anchor: taken.anchor, view: taken.view, withRecording: taken.recordingId !== null, draft: note, notice: NOT_SAVED });
    return;
  }
  pins.push(pinFor({ ...created, body: note.body, kind: note.kind, author_id: accountId }, accountId));
  // The route it was written on, kept from the view we just sent (D-026) -
  // otherwise a fresh entry is the one case we could not judge at all.
  capturedRoute.set(created.id, taken.view.route);
  drawPins();
}

const NOT_SAVED = "Nicht gespeichert - Loupe ist gerade nicht erreichbar. Der Text ist noch da; bitte erneut speichern.";

/** The author's edit, from the application's own window (trial 2026-09-10). */
async function saveEdit(id: string, note: { body: string; kind: NoteKind }): Promise<void> {
  if (!credentials) return;
  let updated: LoadedNote;
  try {
    updated = await api<LoadedNote>(`/api/connections/${credentials.connection}/notes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(note),
    });
  } catch {
    const pin = pins.find((p) => p.id === id);
    if (pin) {
      chrome?.openEditor({ anchor: pin.anchor, view: viewState(), withRecording: false, existing: { id, body: pin.body ?? "", kind: pin.kind ?? "defect" }, draft: note, notice: NOT_SAVED });
    }
    return;
  }
  pins = pins.map((pin) => (pin.id === id ? pinFor(updated, accountId) : pin));
  drawPins();
}

// ---------------------------------------------------------------- record

function startRecording(): void {
  if (isRecording() || mode !== "browse") return; // see isRecording
  setPeek(false);
  layer.setAttribute("data-recording", "true");
  events = [];
  recordStartedAt = new Date().toISOString();
  stopRecording =
    record({
      emit: (event) => {
        events.push(event);
        if (events.length % 25 === 0) {
          post({ type: "loupe:record-state", recording: true, events: events.length });
          chrome?.setRecording(true, events.length);
        }
      },
      blockClass: IGNORE_CLASS,
      maskAllInputs: MASK_INPUTS,
      recordCanvas: false,
      collectFonts: false,
    }) ?? null;
  post({ type: "loupe:record-state", recording: true, events: 0 });
  chrome?.setRecording(true, 0);
}

async function stopRecordingNow(): Promise<void> {
  if (!stopRecording) return;
  stopRecording();
  stopRecording = null;
  layer.removeAttribute("data-recording");
  const view = viewState();
  const meta = {
    startedAt: recordStartedAt,
    stoppedAt: new Date().toISOString(),
    viewport: view.viewport,
    route: view.route,
    build: view.build,
    app: view.app,
    client: view.client,
  };
  const captured = events;
  events = [];
  post({ type: "loupe:record-state", recording: false, events: captured.length });
  chrome?.setRecording(false, 0);

  if (transport?.kind === "socket" && credentials) {
    // The blob goes over HTTP, not the socket: a recording is megabytes and
    // is written once, which is not what a live channel is for.
    const saved = await api<{ id: string }>(`/api/connections/${credentials.connection}/recordings`, {
      method: "POST",
      body: JSON.stringify({ meta, events: captured }),
    });
    // The reviewer just reproduced something; ask what it was while it is
    // still in front of them rather than leaving an orphan recording.
    const anchor = pageAnchor();
    // F-009 here too: the reviewer just reproduced something and is looking at
    // the result. A note asked for at this moment is about THIS view.
    pending = { anchor, view, recordingId: saved.id, snapshot: takeStill() };
    setMode("annotate");
    chrome?.openEditor({ anchor, view, withRecording: true });
  } else {
    post({ type: "loupe:recording", meta, events: captured });
  }
}

function pageAnchor(): Anchor {
  return {
    strategy: "selector",
    testid: null,
    selector: "body",
    tag: "body",
    text: "",
    rect: { x: 0, y: 0, w: 0, h: 0 },
    // A page note points at no element, so there is no spot inside one.
    spot: null,
  };
}

// ---------------------------------------------------------------- an entry's view

/** The element the shell last asked to be shown, and its anchor - kept to follow it on a scroll and a re-render. */
let focused: { anchor: Anchor; el: Element } | null = null;
let focusTimer: number | null = null;
/** How long the frame stays: long enough to find it, short enough not to become part of the page. */
const FOCUS_MS = 8000;

function placeFocus(): void {
  if (!focused) return;
  // A re-render replaced the element: the frame moves to its successor rather than to nowhere.
  if (!focused.el.isConnected) {
    const again = resolveAnchor(focused.anchor).el;
    if (!again) return void (focusBox.style.display = "none");
    focused.el = again;
  }
  const rect = documentRect(focused.el);
  Object.assign(focusBox.style, {
    display: "block",
    left: `${rect.x - 4}px`,
    top: `${rect.y - 4}px`,
    width: `${rect.w + 8}px`,
    height: `${rect.h + 8}px`,
  });
}

/** Scroll an element into view where it is not already, and frame it. */
function markAnchor(anchor: Anchor, el: Element): void {
  const r = el.getBoundingClientRect();
  const inView = r.top >= 0 && r.left >= 0 && r.bottom <= window.innerHeight && r.right <= window.innerWidth;
  if (!inView) el.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
  // A page note points at no element; the page itself is what it marks.
  if (anchor.selector === "body") return;
  focused = { anchor, el };
  placeFocus();
  if (focusTimer !== null) window.clearTimeout(focusTimer);
  focusTimer = window.setTimeout(() => {
    focused = null;
    focusBox.style.display = "none";
  }, FOCUS_MS);
}

/** The anchor's element once the page has drawn it - an application renders after a navigation, not with it. */
function settle(anchor: Anchor, within = 3000): Promise<Resolution> {
  const started = performance.now();
  return new Promise((resolve) => {
    const attempt = () => {
      const found = resolveAnchor(anchor);
      if ((found.el && isRendered(found.el)) || performance.now() - started >= within) return resolve(found);
      window.setTimeout(attempt, 100);
    };
    attempt();
  });
}

/**
 * F-008 (D-049, protocol 8): take this page back to an entry's view - its
 * address, then its scroll position, then its element, scrolled into view and
 * framed - and say what came of it. The answer is the point as much as the
 * navigation: where the view cannot be had, F-008 wants it SAID, and only this
 * side knows whether the element is there.
 *
 * Only within this page's own origin. Another origin cannot be loaded into a
 * page from inside it without leaving the SDK behind, and a shell asking for
 * one is answered `other-origin` rather than obeyed.
 *
 * The address is loaded with `location.assign`, which the browser turns into a
 * fragment navigation where only the `#` part differs - the page and its state
 * stay, as they do in a hash-routed application - and into a fresh document
 * otherwise. A fresh document's SDK announces itself, and the shell asks it
 * again with `navigate: false`; this one answers `navigating` before it goes.
 */
async function showView(message: Extract<ShellMessage, { type: "loupe:show-view" }>): Promise<void> {
  const answer = (result: ShowViewResult) =>
    post({ type: "loupe:view-shown", request: message.request, result, route: viewState().route, build: BUILD });

  // The Rahmen was hidden behind another tab a moment ago, and a frame that
  // was not drawn has no viewport yet: "in view" means nothing until it has.
  if (window.innerWidth === 0 || window.innerHeight === 0) {
    await new Promise((resolve) => {
      const timer = window.setTimeout(resolve, 1000);
      window.addEventListener("resize", () => (window.clearTimeout(timer), resolve(undefined)), { once: true });
    });
  }

  let target: URL;
  try {
    target = new URL(message.href, location.href);
  } catch {
    return answer("other-route");
  }
  if (target.origin !== location.origin) return answer("other-origin");
  if (target.href !== location.href) {
    if (!message.navigate) return answer("other-route");
    location.assign(target.href);
    if (location.href !== target.href) return answer("navigating");
    // A fragment navigation: the page stays and re-renders on `hashchange`,
    // which is queued, not done. Resolving now would find the OLD view's
    // elements wherever the two share a handle.
    await new Promise((resolve) => window.setTimeout(resolve, 120));
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  const found = await settle(message.anchor);
  if (message.scroll) window.scrollTo({ left: message.scroll.x, top: message.scroll.y, behavior: "instant" });
  drawPins();
  if (!found.el) return answer("anchor-missing");
  if (!isRendered(found.el)) return answer("anchor-hidden");
  markAnchor(message.anchor, found.el);
  answer(found.stale ? "shown-weak" : "shown");
}

// ---------------------------------------------------------------- commands

function onCommand(message: ShellMessage): void {
  switch (message.type) {
    case "loupe:hello":
      // An answer, and marked as one: the shell greets every ready that is
      // not, and before protocol 4 that was every ready - D-017.
      post({ type: "loupe:ready", protocol: LOUPE_PROTOCOL, view: viewState(), reply: true });
      break;
    case "loupe:set-mode":
      setMode(message.mode);
      break;
    case "loupe:set-prefs":
      prefs = message.prefs;
      layer.setAttribute("data-pins", String(prefs.pins));
      // The reviewer's pin colour (2026-09-28); a side on an older shell sends none and keeps the red.
      if (prefs.pinColor) layer.style.setProperty("--loupe-pin", prefs.pinColor);
      else layer.style.removeProperty("--loupe-pin");
      setPeek(pinsTakeClicks());
      holding = false;
      showEffective();
      break;
    case "loupe:start-record":
      startRecording();
      break;
    case "loupe:stop-record":
      void stopRecordingNow();
      break;
    case "loupe:set-pins": {
      /*
       * [F-006] V0.4 / Q66: the pending anchor IS the lock - the editor was
       * opened with a context, and while that context stands the selection
       * stays shut. Clearing it here unconditionally is what broke that: the
       * dashboard sends the pin list on EVERY change of `notes`, which lands
       * moments after a note is anchored, so the lock fell while the reviewer
       * was still typing. That was D-002's frame half.
       *
       * The pending anchor is cleared only when the note it belongs to has
       * actually arrived - a pin on the same element that was not in the list
       * before.
       */
      const before = new Set(pins.map((pin) => pin.id));
      pins = message.pins;
      if (pending) {
        const arrived = pins.some(
          (pin) => !before.has(pin.id) && pin.anchor.selector === pending!.anchor.selector,
        );
        if (arrived) pending = null;
      }
      drawPins();
      break;
    }
    case "loupe:cancel-pending":
      pending = null;
      drawPins();
      break;
    case "loupe:focus-pin": {
      // No shell has sent it since CD-015 took the list beside the frame; kept
      // for one that still does, and marking as `loupe:show-view` marks.
      const pin = pins.find((p) => p.id === message.id);
      const found = pin ? resolveAnchor(pin.anchor) : null;
      if (pin && found?.el) markAnchor(pin.anchor, found.el);
      break;
    }
    case "loupe:show-view":
      void showView(message);
      break;
  }
}

// ---------------------------------------------------------------- wiring

/**
 * Redraw when the page might have moved under the pins - D-003.
 *
 * Filtering pins by "is the element there" only helps if somebody re-asks the
 * question. Before this, only `resize` redrew: `popstate`, `hashchange` and a
 * patched `pushState` merely ANNOUNCED, so in a client-routed application the
 * pins of the previous view simply stayed on screen. The layer hangs off
 * `document.body`, outside whatever the app re-renders, so nothing removed
 * them either.
 *
 * A route change is not enough on its own. The demo's own filter re-renders
 * the list without touching the URL, and a reviewed application may show and
 * hide anything at any time - so the trigger is the DOM itself.
 *
 * Two things keep that from being expensive or circular: the callback is
 * debounced to one animation frame, and mutations inside our own layer are
 * ignored - `drawPins` writes there, and reacting to its own writes is an
 * infinite loop rather than a redraw.
 */
function watchTheDom(): void {
  let queued = false;
  const observer = new MutationObserver((records) => {
    if (queued) return;
    if (records.every((r) => isOurs(r.target))) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      drawPins();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: false });
}

function watchNavigation(): void {
  const announce = () => {
    // Redraw BEFORE announcing: the shell may act on the new view, and it
    // should not see a frame in which the old view's pins are still up.
    drawPins();
    post({ type: "loupe:navigated", view: viewState() });
  };
  for (const name of ["pushState", "replaceState"] as const) {
    const original = history[name];
    history[name] = function (this: History, ...args: Parameters<History["pushState"]>) {
      const result = original.apply(this, args);
      queueMicrotask(announce);
      return result;
    } as History[typeof name];
  }
  window.addEventListener("popstate", announce);
  window.addEventListener("hashchange", announce);
}

/**
 * Whether a Loupe session holds this page (D-057). Loaded is not armed: the
 * SDK sits in the application's every page, for every user, and until Loupe
 * speaks it does nothing - no listener, no layer, no patched history, no
 * observer. Before 2026-10-15 all of that went on at load, and on the live
 * application, opened by somebody who was not reviewing anything, holding Ctrl
 * showed Kommentieren, Ctrl+click was swallowed and a still serialized, and
 * Ctrl+Space never reached the application.
 *
 * What arms it is the shell's first word, on both surfaces: in a frame the
 * transport passes on only what comes from Loupe's origin, beginning with
 * `loupe:hello` (D-053); over a socket only what a dashboard in the same room
 * sends - an open socket alone proves nothing, the server accepts the upgrade
 * before it checks the token.
 */
let armed = false;
let socketState: SocketState | null = null;

function fromShell(message: ShellMessage): void {
  arm();
  onCommand(message);
}

function arm(): void {
  if (armed) return;
  armed = true;
  document.head.appendChild(style);
  layer.appendChild(highlight);
  layer.appendChild(indicator);
  layer.appendChild(focusBox);
  document.body.appendChild(layer);
  // D-059: a pin on something fixed, sticky or in a scrolling panel follows it.
  document.addEventListener("scroll", onScroll, { capture: true, passive: true });

  window.addEventListener("mousemove", onMove, true);
  window.addEventListener("keydown", peekFrom, true);
  window.addEventListener("keyup", peekFrom, true);
  window.addEventListener("keydown", holdFrom, true);
  window.addEventListener("keyup", holdFrom, true);
  window.addEventListener("keydown", onKey, true);
  window.addEventListener("blur", () => {
    setPeek(pinsTakeClicks());
    holding = false;
    showEffective();
  });
  window.addEventListener("click", onClick, true);
  window.addEventListener("auxclick", onAuxClick, true);
  window.addEventListener("contextmenu", swallowSwitch, true);
  window.addEventListener("mouseup", swallowSwitch, true);
  window.addEventListener("mousedown", swallow, true);
  window.addEventListener("submit", swallow, true);
  window.addEventListener("resize", () => {
    drawPins();
    post({ type: "loupe:navigated", view: viewState() });
  });
  watchNavigation();
  watchTheDom();
  watchAppStatement();

  if (transport?.kind !== "socket") return;
  // The window's own bar, now that a dashboard has spoken - not before, or a
  // made-up link would put Loupe's chrome over somebody's working application.
  chrome = createChrome({
    onMode: setMode,
    onRecord: (recording) => (recording ? startRecording() : void stopRecordingNow()),
    onNote: (note) => void saveNote(note),
    onUpdate: (id, note) => void saveEdit(id, note),
    onDiscard: () => {
      pending = null;
      drawPins();
    },
    // The project in Loupe, on its Review tab - where the shell that opened
    // this window is. Until 0009 the mark opened the pass, which is gone.
    onOpenDashboard: () => {
      window.open(projectId ? `${credentials!.origin}/projekt/${projectId}?modus=review` : credentials!.origin, "loupe-dashboard");
    },
  });
  chrome.mount();
  if (socketState) chrome.setConnection(socketState);
  void loadPins();
}

function boot(): void {
  const framed = window.parent !== window;
  credentials = framed ? null : readCredentials();

  if (framed) {
    // Only Loupe's own origin may be the shell, and only Loupe hears that we
    // are here (D-053): a page that frames the application from anywhere else
    // gets neither the announcement nor an ear for its commands.
    // An origin, not a URL: `https://loupe.example/` must still match the
    // `https://loupe.example` a MessageEvent carries.
    const shellOrigin = asOrigin(loupeOrigin(null));
    transport = frameTransport(shellOrigin);
    transport.onCommand(fromShell);
    // The shell greets us, but a reload inside the frame has to announce
    // itself - otherwise the app comes back and the shell never knows.
    window.parent.postMessage(
      { type: "loupe:ready", protocol: LOUPE_PROTOCOL, view: viewState() },
      shellOrigin,
    );
    return;
  }

  if (!credentials) return; // nobody asked for a review; nothing is armed (D-057)

  transport = socketTransport({
    origin: credentials.origin,
    connection: credentials.connection,
    token: credentials.token,
    onOpen: () => post({ type: "loupe:ready", protocol: LOUPE_PROTOCOL, view: viewState() }),
    // [F-014] in the application's own window, where the shell's badge is not
    // in sight - and D-024's restart named as one. Kept until the bar exists.
    onState: (state) => {
      socketState = state;
      chrome?.setConnection(state);
    },
  });
  transport.onCommand(fromShell);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
